import React, { useState } from "react";
import { HStack, Spinner, Switch, Text } from "@chakra-ui/react";
import useNotification from "../../../utils/useNotification";
import services from "../services";

const singleInvoiceLabelProps = {
  fontSize: "0.8rem",
  color: "gray.600",
  fontFamily: "'Nunito','Inter', sans-serif",
  fontWeight: "800",
  textTransform: "uppercase" as const,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap" as const,
};

interface SingleInvoiceToggleProps {
  orderId: string;
  isChecked: boolean;
  isLocked: boolean;
  refetchAllOrders: () => void;
  /** Called after the order billing flag is saved successfully */
  onAfterToggleSuccess?: (enabled: boolean) => void | Promise<void>;
}

export const SingleInvoiceToggle: React.FC<SingleInvoiceToggleProps> = ({
  orderId,
  isChecked,
  isLocked,
  refetchAllOrders,
  onAfterToggleSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [localChecked, setLocalChecked] = React.useState(isChecked);
  const toast = useNotification();

  React.useEffect(() => {
    setLocalChecked(isChecked);
  }, [isChecked]);

  const handleToggle = async () => {
    if (isLocked) {
      toast({
        title: "Cannot change",
        description: isChecked
          ? "Single Invoice cannot be changed after single invoice has been generated."
          : "Single Invoice cannot be changed because a confirmed order has been created.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    const targetState = !localChecked;
    try {
      await services.updateOrder(orderId, {
        billingDetails: {
          isSingleGaraazInvoiceRequired: targetState,
        },
      });

      setLocalChecked(targetState);

      let postToggleSyncFailed = false;
      try {
        await onAfterToggleSuccess?.(targetState);
      } catch (syncError) {
        postToggleSyncFailed = true;
        console.error(
          "Single invoice post-toggle split sync failed",
          syncError,
        );
      }

      toast({
        title: "Updated",
        description: postToggleSyncFailed
          ? "Single invoice updated, but split sync failed. Please refresh and verify split configuration."
          : targetState
            ? "Single invoice enabled. All splits transaction type has been changed to Trading."
            : "Single invoice requirement removed.",
        status: postToggleSyncFailed ? "warning" : "success",
        duration: 3000,
        isClosable: true,
      });
      refetchAllOrders();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update Single Invoice.";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HStack spacing={2} align="center">
      {isLoading ? (
        <Spinner size="xs" color="gray.500" />
      ) : (
        <Switch
          size="sm"
          colorScheme="blue"
          isChecked={localChecked}
          isDisabled={isLocked}
          onChange={handleToggle}
        />
      )}
      <Text {...singleInvoiceLabelProps}>SINGLE INVOICE</Text>
    </HStack>
  );
};
